import type { Dayjs } from "dayjs"
import type { TFunction } from "i18next"
import { dayjs } from "@/helpers/dayjs"
import type { ITransaction } from "@/store/apis/withdrawalAPI"

export interface IOperationsDateGroup {
  dayKey: string
  label: string
  items: ITransaction[]
}

const buildLabel = (
  txDay: Dayjs,
  today: Dayjs,
  yesterday: Dayjs,
  t: TFunction
): string => {
  if (txDay.isSame(today, "day")) return t("wallet.withdraw.history.today")
  if (txDay.isSame(yesterday, "day"))
    return t("wallet.withdraw.history.yesterday")
  const sameYear = txDay.year() === today.year()
  return sameYear ? txDay.format("D MMMM") : txDay.format("D MMMM YYYY")
}

export const groupOperationsByDate = (
  transactions: ITransaction[],
  t: TFunction
): IOperationsDateGroup[] => {
  const groups = new Map<string, IOperationsDateGroup>()
  const today = dayjs().startOf("day")
  const yesterday = today.subtract(1, "day")

  for (const tx of transactions) {
    const txDay = dayjs(tx.createdAt).startOf("day")
    const dayKey = txDay.format("YYYY-MM-DD")

    let group = groups.get(dayKey)
    if (!group) {
      group = {
        dayKey,
        label: buildLabel(txDay, today, yesterday, t),
        items: []
      }
      groups.set(dayKey, group)
    }
    group.items.push(tx)
  }

  // Sort items inside each group by createdAt desc,
  // then sort groups by dayKey desc (YYYY-MM-DD lex-compares correctly).
  for (const group of groups.values()) {
    group.items.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }
  return Array.from(groups.values()).sort((a, b) =>
    b.dayKey.localeCompare(a.dayKey)
  )
}
